// How a destination's params travel in a URL.
//
// Four kinds, and every param a destination declares is one of them. Declared
// as data rather than as a validator so ONE declaration answers all three
// questions a link asks — is this value acceptable, how is it written into a
// URL, how is it read back out — and the three cannot disagree.

/** One param a destination understands. */
export type ParamSpec =
  /** One value from a closed set. */
  | { readonly kind: 'enum'; readonly values: readonly string[] }
  /** Several values from a closed set, comma-joined in the URL. */
  | { readonly kind: 'enums'; readonly values: readonly string[] }
  /** On when present, written `1`. There is no "off": off is absent. */
  | { readonly kind: 'flag' }
  /** An opaque id or reference — a record the page opens. */
  | { readonly kind: 'token' };

export type ParamSpecs = Readonly<Record<string, ParamSpec>>;

/** The value a spec accepts, as TypeScript sees it. */
export type ParamValue<S extends ParamSpec> = S extends { kind: 'enum'; values: readonly (infer V)[] }
  ? V
  : S extends { kind: 'enums'; values: readonly (infer V)[] }
    ? V[]
    : S extends { kind: 'flag' }
      ? true
      : string;

/** A destination's params: every one optional, because absent is the page's default. */
export type ParamsOf<P extends ParamSpecs> = { [K in keyof P]?: ParamValue<P[K]> };

export const oneOf = <const V extends readonly string[]>(values: V) => ({ kind: 'enum', values }) as const;
export const someOf = <const V extends readonly string[]>(values: V) => ({ kind: 'enums', values }) as const;
export const flag = () => ({ kind: 'flag' }) as const;
export const token = () => ({ kind: 'token' }) as const;

/**
 * How long a token may be. Every id D-ORCA addresses a record by — a Mongo id,
 * a UUID, a delivery ref, an item key — is far shorter; a longer one is not an
 * id, and a URL is not where to find that out.
 */
const MAX_TOKEN = 256;
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const CONTROL = /[ -]/;

function isToken(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value !== '' &&
    value.trim() === value &&
    value.length <= MAX_TOKEN &&
    !CONTROL.test(value)
  );
}

/**
 * The value, if this spec accepts it; `undefined` if not.
 *
 * A list with ONE bad member is refused whole rather than trimmed: "planned or
 * rejected" minus a member is a different set, and a link that opens a
 * different set than it promised is the failure this module exists to prevent.
 */
export function acceptValue(spec: ParamSpec, value: unknown): unknown {
  switch (spec.kind) {
    case 'enum':
      return typeof value === 'string' && spec.values.includes(value) ? value : undefined;
    case 'enums':
      return Array.isArray(value) &&
        value.length > 0 &&
        value.every((member) => typeof member === 'string' && spec.values.includes(member))
        ? [...new Set(value as string[])]
        : undefined;
    case 'flag':
      return value === true ? true : undefined;
    case 'token':
      return isToken(value) ? value : undefined;
  }
}

/** An accepted value, as the URL writes it. */
export function encodeValue(spec: ParamSpec, value: unknown): string {
  switch (spec.kind) {
    case 'enums':
      return (value as string[]).join(',');
    case 'flag':
      return '1';
    case 'enum':
    case 'token':
      return value as string;
  }
}

/** A URL's string, back into the shape `acceptValue` judges. */
export function decodeValue(spec: ParamSpec, raw: string): unknown {
  switch (spec.kind) {
    case 'enums':
      return raw.split(',');
    case 'flag':
      return raw === '1' ? true : raw;
    case 'enum':
    case 'token':
      return raw;
  }
}
